import type { AccessChallenge, AccessErrorCode, AccessSession } from "@shared/access.types";
import { fetchJson, HttpError } from "./http";

export class AccessError extends Error {
	constructor(readonly code: AccessErrorCode) {
		super(code);
	}
}

/**
 * Le contrat que les écrans d'accès attendent : un refus du serveur leur arrive en
 * `AccessError`, avec un code qu'ils savent traduire en phrase.
 *
 * ⚠️ Une coupure réseau et un délai dépassé ne sont PAS des refus, et l'`HttpError`
 * remonte alors telle quelle. Les traduire en `LINK_UNKNOWN` afficherait « ce lien
 * n'est pas valide, demandez-en un nouveau à votre école » à une entreprise dont le
 * lien est parfait, et l'enverrait réclamer un lien qui ne changerait rien. C'est déjà
 * ce qui se passait quand `fetch` rejetait : l'appelant voit que ce n'est pas une
 * `AccessError` et sort son message de repli.
 */
async function post<T>(path: string, body: unknown): Promise<T> {
	try {
		return await fetchJson<T>(path, { method: "POST", body });
	} catch (error) {
		// `code` ne vaut quelque chose que si le serveur a répondu. Null, c'est qu'il
		// n'a rien dit du tout : ce n'est pas à nous d'inventer ce qu'il aurait dit.
		const code = error instanceof HttpError ? error.code : null;
		if (code === null) throw error;
		// ⚠️ Un 5xx n'est pas un refus non plus, et il rendait pourtant `LINK_UNKNOWN`.
		// Base injoignable : `app.onError` répond 503 `SERVICE_UNAVAILABLE`, un code que
		// le dictionnaire ne connaît pas, donc on repliait sur « ce lien n'est pas
		// valide, demandez-en un nouveau à votre école ». L'entreprise écrivait à son
		// école, l'école lui renvoyait un lien neuf, et il échouait pareil.
		if (estPanneServeur(error)) throw error;
		throw new AccessError(estCodeAffichable(code) ? code : "LINK_UNKNOWN");
	}
}

/** Le serveur a répondu, mais pour dire qu'il ne va pas bien. Ce n'est pas un refus. */
function estPanneServeur(error: unknown): boolean {
	return error instanceof HttpError && error.status !== null && error.status >= 500;
}

export const api = {
	requestCode: (token: string) => post<AccessChallenge>("/api/access/request", { token }),
	verifyCode: (token: string, code: string) =>
		post<AccessSession>("/api/access/verify", { token, code }),

	/**
	 * `null` veut dire « le serveur a répondu qu'il n'y a pas de session » : c'est une
	 * réponse, et l'écran a le droit d'en tirer « aucun accès ouvert ».
	 *
	 * ⚠️ Une coupure ou un délai dépassé ne rendent donc PAS `null`, ils lèvent. On ne
	 * sait pas si la session tient, et répondre `null` ferait dire à l'écran « aucun
	 * accès ouvert, ouvrez le lien reçu par mail » à une entreprise dont l'accès est
	 * valide : elle irait fouiller un vieux mail pour un problème de wifi. Le message
	 * juste existe déjà (`copy.share.failed`), il faut donc que l'appelant attrape.
	 */
	async currentSession(): Promise<AccessSession | null> {
		try {
			return await fetchJson<AccessSession>("/api/access/me");
		} catch (error) {
			// Un 5xx non plus ne dit pas « pas de session » : il dit que le serveur ne
			// sait pas répondre. Le rendre `null` afficherait « aucun accès ouvert »
			// pendant une panne de base, à quelqu'un dont la session est valide.
			if (estPanneServeur(error)) throw error;
			if (error instanceof HttpError && error.status !== null) return null;
			throw error;
		}
	},
};

/** Messages destinés au visiteur. Jamais le code technique brut à l'écran. */
export const ACCESS_MESSAGES: Record<AccessErrorCode, string> = {
	LINK_UNKNOWN: "Ce lien n'est pas valide. Demandez-en un nouveau à votre école.",
	LINK_EXPIRED: "Ce lien a expiré. Demandez-en un nouveau à votre école.",
	LINK_REVOKED: "L'école a fermé cet accès.",
	CODE_INVALID: "Ce code n'est pas le bon.",
	CODE_EXPIRED: "Ce code a expiré. Demandez-en un nouveau.",
	TOO_MANY_ATTEMPTS: "Trop d'essais. Demandez un nouveau code.",
	RATE_LIMITED: "Trop de tentatives. Réessayez dans une minute.",
};

/**
 * Un code n'est utilisable que si le dictionnaire ci-dessus sait le dire : les écrans
 * y lisent la phrase sans se protéger, et un code inconnu du serveur rendrait une
 * alerte vide. La garde se dérive donc du dictionnaire au lieu de recopier la liste
 * des codes, qui divergerait le jour où le serveur en ajoute un.
 */
function estCodeAffichable(code: string): code is AccessErrorCode {
	return code in ACCESS_MESSAGES;
}
